import { CLASS_NAMES, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for handling hero video play/pause functionality
 */
export default class ImageGalleryGrid extends MessageBus {
  constructor(element) {
    super();
    this.element = element;

    // Lightbox & slides
    this.lightbox = this.element.querySelector(
      `.${CLASS_NAMES.IMAGE_GRID_LIGHTBOX}`
    );
    this.slides = Array.from(
      this.element.querySelectorAll(`.${CLASS_NAMES.IMAGE_GRID_LIGHTBOX_SLIDE}`)
    );
    this.closeButtons = Array.from(
      this.element.querySelectorAll(`.${CLASS_NAMES.IMAGE_GRID_LIGHTBOX_CLOSE}`)
    );
    this.nextButtons = Array.from(
      this.element.querySelectorAll(`.${CLASS_NAMES.IMAGE_GRID_LIGHTBOX_NEXT}`)
    );
    this.prevButtons = Array.from(
      this.element.querySelectorAll(`.${CLASS_NAMES.IMAGE_GRID_LIGHTBOX_PREV}`)
    );

    this.currentIndex = 0;

    // Bind methods
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleSwipeStart = this.handleSwipeStart.bind(this);
    this.handleSwipeEnd = this.handleSwipeEnd.bind(this);
    this.closeLightbox = this.closeLightbox.bind(this);

    this.init();
  }

  init() {
    this.setupHandlers().enable();
    this.hideAllSlides();
    this.setupScrollAnimations();
    this.setupLoadMore();
    return this;
  }

  setupHandlers() {
    // Arrow buttons
    this.nextHandler = () => this.showSlide(this.currentIndex + 1);
    this.prevHandler = () => this.showSlide(this.currentIndex - 1);

    // Click / keyboard on card images
    const cardFigures = Array.from(
      this.element.querySelectorAll(
        `.${CLASS_NAMES.IMAGE_GRID_CARD_CONTAINER} figure[role="button"]`
      )
    );

    cardFigures.forEach((figure) => {
      const index = parseInt(
        figure.querySelector('img').dataset.lightboxIndex,
        10
      );

      figure.addEventListener(EVENTS.CLICK, () => {
        this.openLightbox(index);
      });

      figure.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.openLightbox(index);
        }
      });
    });

    // Click on card overlays
    const cardOverlays = Array.from(
      this.element.querySelectorAll(`.${CLASS_NAMES.IMAGE_GRID_CARD_OVERLAY}`)
    );

    cardOverlays.forEach((overlay) => {
      overlay.addEventListener(EVENTS.CLICK, () => {
        const index = parseInt(overlay.dataset.lightboxIndex, 10);
        this.openLightbox(index);
      });
    });

    return this;
  }

  enable() {
    // Close buttons
    this.closeButtons.forEach((btn) =>
      btn.addEventListener(EVENTS.CLICK, this.closeLightbox)
    );

    // Next/Prev buttons
    this.nextButtons.forEach((btn) =>
      btn.addEventListener(EVENTS.CLICK, this.nextHandler)
    );
    this.prevButtons.forEach((btn) =>
      btn.addEventListener(EVENTS.CLICK, this.prevHandler)
    );

    document.addEventListener(EVENTS.KEY_DOWN, this.handleKeydown);

    if (this.slides.length) {
      this.slides.forEach((slide) => {
        slide.addEventListener('touchstart', this.handleSwipeStart, {
          passive: true
        });
        slide.addEventListener('touchend', this.handleSwipeEnd);
      });
    }

    // Close lightbox when clicking outside content
    if (this.lightbox) {
      this.lightbox.addEventListener(EVENTS.CLICK, (e) => {
        const content = this.lightbox.querySelector(
          `.${CLASS_NAMES.IMAGE_GRID_CONTENT}`
        );
        if (content && !content.contains(e.target)) {
          this.closeLightbox();
        }
      });
    }
  }

  setupScrollAnimations() {
    const cards = this.element.querySelectorAll(
      `.${CLASS_NAMES.IMAGE_GRID_CARD_CONTAINER}`
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(CLASS_NAMES.IN_VIEWPORT);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    const getColumns = () => (window.innerWidth >= 1200 ? 3 : 2);

    cards.forEach((card, i) => {
      const group = Math.floor(i / getColumns());
      card.classList.add(
        group % 2 === 0 ? CLASS_NAMES.FADE_RIGHT : CLASS_NAMES.FADE_LEFT
      );
      observer.observe(card);
    });

    window.addEventListener('resize', () => {
      const columns = getColumns();
      cards.forEach((card, i) => {
        card.classList.remove(CLASS_NAMES.FADE_LEFT, CLASS_NAMES.FADE_RIGHT);
        const group = Math.floor(i / columns);
        card.classList.add(
          group % 2 === 0 ? CLASS_NAMES.FADE_RIGHT : CLASS_NAMES.FADE_LEFT
        );
      });
    });
  }

  openLightbox(index = 0) {
    this.currentIndex = index;
    this.showSlide(index);

    if (!this.lightbox) return;

    this.lightbox.classList.add(CLASS_NAMES.ACTIVE);
    this.lightbox.setAttribute('role', 'dialog');
    this.lightbox.setAttribute('aria-modal', 'true');
    this.lightbox.setAttribute('aria-hidden', 'false');
    this.previousActiveElement = document.activeElement;

    // Focus first element (close button preferred)
    requestAnimationFrame(() => this.focusFirstElement());
  }

  closeLightbox() {
    if (!this.lightbox) return;

    this.lightbox.classList.remove(CLASS_NAMES.ACTIVE);
    this.lightbox.setAttribute('aria-hidden', 'true');
    this.hideAllSlides();

    if (this.previousActiveElement) {
      this.previousActiveElement.focus();
    }
  }

  focusFirstElement() {
    const closeBtn = this.lightbox.querySelector(
      `.${CLASS_NAMES.IMAGE_GRID_LIGHTBOX_CLOSE}`
    );
    if (closeBtn) {
      closeBtn.focus();
    } else {
      const focusable = this.getFocusableElements();
      if (focusable.length) {
        focusable[0].focus();
      } else {
        this.lightbox.focus();
      }
    }
  }

  getFocusableElements() {
    return Array.from(
      this.lightbox.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.hasAttribute('disabled') && el.offsetParent !== null);
  }

  showSlide(index) {
    if (!this.slides.length) return;

    this.currentIndex = (index + this.slides.length) % this.slides.length;
    this.slides.forEach((slide, i) => {
      slide.classList.toggle(CLASS_NAMES.ACTIVE, i === this.currentIndex);
      slide.setAttribute('aria-hidden', i !== this.currentIndex);
    });
  }

  hideAllSlides() {
    this.slides.forEach((slide) => {
      slide.classList.remove(CLASS_NAMES.ACTIVE);
      slide.setAttribute('aria-hidden', true);
    });
  }

  handleKeydown(e) {
    if (!this.lightbox || !this.lightbox.classList.contains(CLASS_NAMES.ACTIVE))
      return;

    switch (e.key) {
      case 'ArrowRight':
        this.showSlide(this.currentIndex + 1);
        break;
      case 'ArrowLeft':
        this.showSlide(this.currentIndex - 1);
        break;
      case 'Escape':
        this.closeLightbox();
        break;
      case 'Tab':
        this.trapFocus(e);
        break;
      default:
        break;
    }
  }

  trapFocus(e) {
    const focusable = this.getFocusableElements();
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  handleSwipeStart(e) {
    this.startX = e.changedTouches[0].clientX;
  }

  handleSwipeEnd(e) {
    if (this.startX == null) return;

    const diff = e.changedTouches[0].clientX - this.startX;
    if (diff > 50) this.showSlide(this.currentIndex - 1);
    else if (diff < -50) this.showSlide(this.currentIndex + 1);

    this.startX = null;
  }

  setupLoadMore() {
    const cards = Array.from(
      this.element.querySelectorAll(`.${CLASS_NAMES.IMAGE_GRID_CARD_CONTAINER}`)
    );
    const loadMoreBtn = this.element.querySelector(
      `.${CLASS_NAMES.IMAGE_GRID_LOAD_MORE}`
    );

    if (!loadMoreBtn) return;

    const increment = 6;
    let currentVisible = 6;

    loadMoreBtn.addEventListener(EVENTS.CLICK, (e) => {
      e.preventDefault();
      const nextVisible = currentVisible + increment;

      for (
        let i = currentVisible;
        i < nextVisible && i < cards.length;
        i += 1
      ) {
        cards[i].classList.remove(CLASS_NAMES.IS_HIDDEN);
      }

      currentVisible = nextVisible;

      if (currentVisible >= cards.length) {
        loadMoreBtn.style.display = 'none';
      }
    });
  }

  destroy() {
    this.closeButtons.forEach((btn) =>
      btn.removeEventListener(EVENTS.CLICK, this.closeLightbox)
    );
    this.nextButtons.forEach((btn) =>
      btn.removeEventListener(EVENTS.CLICK, this.nextHandler)
    );
    this.prevButtons.forEach((btn) =>
      btn.removeEventListener(EVENTS.CLICK, this.prevHandler)
    );
    document.removeEventListener(EVENTS.KEY_DOWN, this.handleKeydown);

    if (this.slides.length) {
      this.slides.forEach((slide) => {
        slide.removeEventListener('touchstart', this.handleSwipeStart);
        slide.removeEventListener('touchend', this.handleSwipeEnd);
      });
    }
  }
}
